<template>
  <div class="task-overview-page">
    <div class="user-tasks__header no-print">
      <div class="user-tasks__header__left">
        <h2 class="user-tasks__header__left__inner">
          <h2 v-if="!isCanSeeOtherUsers">{{ viewedUser.name }}</h2>
          <SelectUser v-else :preSelectedUserId="user_id" :userName="user_name" path="tasks">
          </SelectUser>
          <div class="no-mobile">-</div>
          <p>{{ $t('vue2.task_overview.task_overview') }}</p>
        </h2>
        <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
      </div>
      <div class="user-tasks__header__right">
        <TaskOverviewVersions
          v-if="viewedUser && canUserSeeVersions"
          :user="viewedUser">
        </TaskOverviewVersions>
        <button class="no-mobile marg-1-left" @click="print">
          <span class="icon-printer3"></span>
        </button>
      </div>
    </div>
    <TaskOverviewLive :user="viewedUser"></TaskOverviewLive>
  </div>
</template>

<script>
import TaskOverviewLive from './TaskOverviewLive'
import TaskOverviewVersions from './TaskOverview'
import ChangeSchoolYear from '../../School/ChangeSchoolYear'
import SelectUser from '../../common/SelectUser'

// Side-wrapper til Rails-viewet users/tasks.html.erb ("Opgaveoversigt"), der mounter
// <user-tasks user_id="..." user_calendar_id="..." user_name="...">
export default {
  components: {TaskOverviewLive, TaskOverviewVersions, ChangeSchoolYear, SelectUser},
  props: {
    user_id: {
      default: null
    },
    user_calendar_id: {
      default: null
    },
    user_name: {
      default: ''
    },
    isCanSeeOtherUsers: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    loggedInUser () {
      return this.$store.state.user
    },
    viewedUser () {
      return {
        id: this.user_id ? this.user_id * 1 : null,
        name: this.user_name,
        user_calendar_id: this.user_calendar_id ? this.user_calendar_id * 1 : null
      }
    },
    canUserSeeVersions () {
      if (!this.loggedInUser || !this.viewedUser || !this.viewedUser.id) {
        return false
      }
      if (this.userHasPermission('can_manage_school')) {
        return true
      }
      return (this.loggedInUser.id * 1) === (this.viewedUser.id * 1) && this.userHasPermission('can_see_own_task_overview')
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    usersSorted () {
      return this.$store.getters.usersSortedOnName
    }
  },
  methods: {
    userHasPermission (permission) {
      if (!this.loggedInUser) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.loggedInUser.admin
      }
      return true
    },
    print () {
      const source = this.$el.querySelector('.to-user')
      if (!source) {
        return
      }
      const title = this.$t('vue2.task_versions.task_overview')
      let stylesHtml = ''
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"][href*="/app/static/css/"], style')]) {
        stylesHtml += node.outerHTML
      }
      const printStyles = `<style>
        html, body { background: #fff; margin: 0; }
        .to-user { page-break-after: auto !important; break-after: auto !important; }
        .only-print { display: flex !important; }
        .no-print { display: none !important; }
      </style>`
      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')
      WinPrint.document.write(`<!DOCTYPE html><html id="html-bg-white"><head><title>${title}</title>${stylesHtml}${printStyles}</head><body><div id="printTaskOverviews">${source.outerHTML}</div></body></html>`)
      const afterPrint = () => {
        WinPrint.close()
      }
      WinPrint.onafterprint = afterPrint
      setTimeout(_ => {
        WinPrint.document.close()
        WinPrint.focus()
        WinPrint.print()
      }, 1000)
    },
    selectUser (id) {
      window.location = '/users/' + id + '/tasks#'
    },
    selectNextUser () {
      const idx = this.usersSorted.findIndex(user => (user.id * 1) === (this.user_id * 1))
      const nextUserId = this.usersSorted[idx + 1] && this.usersSorted[idx + 1].id ? this.usersSorted[idx + 1].id : null
      if (nextUserId) {
        this.selectUser(nextUserId)
      }
    },
    selectPrevUser () {
      const idx = this.usersSorted.findIndex(user => (user.id * 1) === (this.user_id * 1))
      const prevUserId = this.usersSorted[idx - 1] && this.usersSorted[idx - 1].id ? this.usersSorted[idx - 1].id : null
      if (prevUserId) {
        this.selectUser(prevUserId)
      }
    }
  },
  mounted () {
    if (document.title && this.user_name) {
      document.title = this.user_name + ' - ' + this.$t('vue2.task_overview.task_overview')
    }
    this._keyListener = function (e) {
      const key = e.which || e.keyCode
      const metaKey = e.metaKey || e.ctrlKey
      if (metaKey && key === 39) {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          this.selectNextUser()
        }
      }
      if (metaKey && e.shiftKey && key === 37) {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          this.selectPrevUser()
        }
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this._keyListener)
  }
}
</script>

<style>
@import "../../../assets/css/variables.css";

.task-overview-page {
  min-height: calc(100vh - 150px);
}

.user-tasks__header {
  display: flex;
  flex-direction: column;
  margin: 1rem;
  gap: 1rem;
}

.user-tasks__header__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.user-tasks__header__left__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.user-tasks__header__right {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

@media only screen and (min-width: 768px) {
  .user-tasks__header {
    flex-direction: row;
    justify-content: space-between;
    margin: 0 0 1rem 0;
  }
  .user-tasks__header__left {
    flex-direction: row;
  }

  .user-tasks__header__left__inner {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .user-tasks__header__right {
    flex-direction: row;
  }
}
</style>



// WEBPACK FOOTER //
// TaskOverviewPage.vue?563bdb2e